import {
  FORM_CHOICE_TYPES,
  FORM_MULTI_CHOICE_TYPES,
  FORM_RATING_MAX,
  type FormAnswerInput,
  type FormAnswerValue,
  type FormQuestion,
  type FormQuestionSet,
  type FormQuestionType,
} from '../types/form-question';

/**
 * Filling in an event form: what each control holds while someone types, what is wrong with it,
 * and what is finally sent. The checks mirror the server's, so a form fails before the round trip
 * rather than after it.
 */

const CHOICE_TYPES: ReadonlySet<FormQuestionType> = new Set(FORM_CHOICE_TYPES);
const MULTI_CHOICE_TYPES: ReadonlySet<FormQuestionType> = new Set(FORM_MULTI_CHOICE_TYPES);

/** Answered by picking from the question's `options`. */
export const isChoiceType = (type: FormQuestionType) => CHOICE_TYPES.has(type);

/** Answered with a list of picks rather than one. */
export const isMultiChoiceType = (type: FormQuestionType) => MULTI_CHOICE_TYPES.has(type);

/**
 * Drawn as a row of options rather than a single box, so it is labelled by a legend rather than a
 * label: radio buttons, checkboxes, and the 1–5 rating.
 */
export const isGroupType = (type: FormQuestionType) =>
  type === 'radio' || type === 'rating' || isMultiChoiceType(type);

/** A control's value while it is being filled in: the text as typed, or the picks so far. */
export type AnswerDraft = string | string[];

/** Every question's draft, by question ID. */
export type AnswerDrafts = Record<string, AnswerDraft>;

/** Every question of a form, in the order it is asked. */
export const questionsOf = (form: FormQuestionSet): FormQuestion[] => [
  ...form.ungroupedQuestions,
  ...form.sections.flatMap((section) => section.questions),
];

export const blankAnswer = (question: FormQuestion): AnswerDraft =>
  isMultiChoiceType(question.type) ? [] : '';

export const blankAnswers = (form: FormQuestionSet): AnswerDrafts =>
  Object.fromEntries(questionsOf(form).map((q) => [q.id, blankAnswer(q)]));

const isBlank = (draft: AnswerDraft | undefined): boolean =>
  draft === undefined || (Array.isArray(draft) ? draft.length === 0 : draft.trim() === '');

function validIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** What is wrong with one answer, worded for the person filling it in — or nothing. */
export function answerProblem(
  question: FormQuestion,
  draft: AnswerDraft | undefined,
): string | undefined {
  if (draft === undefined || isBlank(draft)) {
    return question.required ? 'This question needs an answer.' : undefined;
  }
  if (Array.isArray(draft)) return undefined;
  const text = draft.trim();
  switch (question.type) {
    case 'text':
      return text.length > 500 ? 'Keep it to 500 characters or fewer.' : undefined;
    case 'textarea':
      return text.length > 5_000 ? 'Keep it to 5,000 characters or fewer.' : undefined;
    case 'number': {
      const value = Number(text);
      if (!Number.isFinite(value)) return 'Enter a number.';
      return Math.abs(value) > 1_000_000_000 ? 'That number is too large.' : undefined;
    }
    case 'rating': {
      const value = Number(text);
      return Number.isInteger(value) && value >= 1 && value <= FORM_RATING_MAX
        ? undefined
        : `Pick a rating from 1 to ${FORM_RATING_MAX}.`;
    }
    case 'date':
      return validIsoDate(text) ? undefined : 'Enter a valid date.';
    default:
      return undefined;
  }
}

/** Each question's problem, by question ID. Empty when the whole form can be sent. */
export function answerProblems(
  form: FormQuestionSet,
  drafts: AnswerDrafts,
): Record<string, string> {
  const problems: Record<string, string> = {};
  for (const question of questionsOf(form)) {
    const problem = answerProblem(question, drafts[question.id]);
    if (problem) problems[question.id] = problem;
  }
  return problems;
}

/** The answers to send: blanks left out, text trimmed, numbers and ratings as numbers. */
export function answerInputs(form: FormQuestionSet, drafts: AnswerDrafts): FormAnswerInput[] {
  return questionsOf(form).flatMap((question): FormAnswerInput[] => {
    const draft = drafts[question.id];
    if (draft === undefined || isBlank(draft)) return [];
    if (Array.isArray(draft)) return [{ questionId: question.id, value: draft }];
    const text = draft.trim();
    const numeric = question.type === 'number' || question.type === 'rating';
    return [{ questionId: question.id, value: numeric ? Number(text) : text }];
  });
}

/**
 * Carries what someone has typed over to a newer copy of the same form, wherever a question kept
 * its wording and type. Picks that are no longer offered are dropped. For when the form was edited
 * while they were filling it in.
 */
export function carryAnswers(
  previous: FormQuestionSet,
  drafts: AnswerDrafts,
  next: FormQuestionSet,
): AnswerDrafts {
  const byWording = new Map(
    questionsOf(previous).map((q) => [`${q.type}\u0000${q.label}`, drafts[q.id]]),
  );
  const carried = blankAnswers(next);
  for (const question of questionsOf(next)) {
    const draft = byWording.get(`${question.type}\u0000${question.label}`);
    if (draft === undefined) continue;
    if (!isChoiceType(question.type)) carried[question.id] = draft;
    else if (Array.isArray(draft))
      carried[question.id] = draft.filter((pick) => question.options.includes(pick));
    else if (question.options.includes(draft)) carried[question.id] = draft;
  }
  return carried;
}

/** A stored answer as one line of text. */
export const answerText = (value: FormAnswerValue) =>
  Array.isArray(value) ? value.join(', ') : String(value);
